import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import type { AssignableStaffMember } from '../../api/employees';
import { pluralize } from '../../utils/customerPicker';
import PersonAvatar from './PersonAvatar';
import PersonRow from './PersonRow';
import PickerPageHeader from './PickerPageHeader';
import PickerSearchBar from './PickerSearchBar';

interface StaffPickerPageProps {
  members: AssignableStaffMember[];
  assignedStaffId: string | null;
  /** null = no one. */
  onPick: (id: string | null) => void;
  onBack: () => void;
  /** Inside another sheet's scroll view: no scroll view of its own. */
  embedded?: boolean;
}

/** Every staff member at the location as a radio list; tap one to assign the order. */
const StaffPickerPage: React.FC<StaffPickerPageProps> = ({
  members,
  assignedStaffId,
  onPick,
  onBack,
  embedded = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      member =>
        member.name.toLowerCase().includes(q) ||
        member.role.toLowerCase().includes(q),
    );
  }, [members, query]);

  const radio = (on: boolean) => (
    <View style={[styles.radio, on && styles.radioOn]} />
  );

  const list = (
    <View style={styles.list} accessibilityRole="radiogroup">
      {!query.trim() ? (
        <PersonRow
          leading={
            <View style={styles.noneAvatar}>
              <Feather
                name="slash"
                size={iconSize(18)}
                color={theme.colors.textGray500}
              />
            </View>
          }
          name="No one"
          meta="Leave unassigned"
          selected={assignedStaffId === null}
          trailing={radio(assignedStaffId === null)}
          onPress={() => onPick(null)}
          accessibilityRole="radio"
          accessibilityLabel="No one, leave unassigned"
        />
      ) : null}
      {filtered.map(member => {
        const selected = assignedStaffId === member.id;
        const role = member.role || 'Staff';
        return (
          <PersonRow
            key={member.id}
            leading={<PersonAvatar name={member.name} />}
            name={member.name}
            meta={role}
            selected={selected}
            trailing={radio(selected)}
            onPress={() => onPick(member.id)}
            accessibilityRole="radio"
            accessibilityLabel={`${member.name}, ${role}`}
          />
        );
      })}
      {filtered.length === 0 ? (
        <Text style={styles.empty}>No staff match “{query.trim()}”</Text>
      ) : null}
    </View>
  );

  return (
    <View style={styles.page}>
      <PickerPageHeader
        title="Assign staff"
        subtitle={`${pluralize(members.length, 'staff member')} at this location · tap to assign`}
        onBack={onBack}
        compact={embedded}
      />
      <View style={[styles.search, embedded && styles.searchEmbedded]}>
        <PickerSearchBar
          value={query}
          onChangeText={setQuery}
          placeholder="Search name or role"
        />
      </View>
      {embedded ? (
        list
      ) : (
        <BottomSheetScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.scroll}>
          {list}
        </BottomSheetScrollView>
      )}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    page: {
      flex: 1,
    },
    search: {
      paddingHorizontal: s(20),
      paddingBottom: v(8),
    },
    searchEmbedded: {
      paddingHorizontal: s(4),
    },
    scroll: {
      paddingBottom: v(24),
    },
    list: {
      paddingHorizontal: s(8),
    },
    noneAvatar: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      borderWidth: borderWidth(1.5),
      borderStyle: 'dashed',
      borderColor: theme.colors.textTertiary,
      backgroundColor: theme.colors.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    radio: {
      width: s(22),
      height: s(22),
      borderRadius: s(11),
      borderWidth: 2,
      borderColor: theme.colors.textTertiary,
    },
    radioOn: {
      borderWidth: s(6.5),
      borderColor: theme.colors.primaryDark,
    },
    empty: {
      textAlign: 'center',
      paddingVertical: v(24),
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textGray500,
    },
  });

export default StaffPickerPage;
